import { ceil, floor, mapValues, dropWhile, isEmpty, minBy, clamp } from "lodash";

/**
 * Interface for a 2D point with x and y coordinates
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Interface for the result of a quadratic Bezier calculation
 */
export interface BezierResult {
  curveY: number;
  angle: number;
}

/**
 * Type for a 2D matrix of numbers
 */
type NumberMatrix = number[][];

/**
 * Performs bilinear interpolation to calculate a value at a specific point
 * 
 * @param roomLocation - The point at which to interpolate the value
 * @param values - 2D matrix of values
 * @param spacing - The spacing between sample points
 * @param xOffset - Offset in the x direction
 * @param yOffset - Offset in the y direction
 * @returns The interpolated value at the given location
 */
export const bilinearInterpolate = (
  roomLocation: Point,
  values: NumberMatrix,
  spacing: number,
  xOffset: number,
  yOffset: number
): number => {
  const x = roomLocation.x;
  const y = roomLocation.y;

  // Calculate relative position in grid
  const xRelative = (x - xOffset) / spacing;
  const yRelative = (y - yOffset) / spacing;
  const maxXIndex = values[0].length - 1;
  const maxYIndex = values.length - 1;

  // Clamp to grid boundaries instead of returning Infinity
  // This extends edge values to cover the entire room
  const xRel = Math.max(0, Math.min(maxXIndex, xRelative));
  const yRel = Math.max(0, Math.min(maxYIndex, yRelative));

  // Use clamped relative positions for index calculation
  const idxX0 = Math.min(Math.max(floor(xRel), 0), values[0].length - 1);
  const idxX1 = Math.min(Math.max(ceil(xRel), 0), values[0].length - 1);
  const idxY0 = Math.min(Math.max(floor(yRel), 0), values.length - 1);
  const idxY1 = Math.min(Math.max(ceil(yRel), 0), values.length - 1);

  const x0 = xOffset + idxX0 * spacing;
  const x1 = xOffset + idxX1 * spacing;
  const y0 = yOffset + idxY0 * spacing;
  const y1 = yOffset + idxY1 * spacing;

  const c00 = values[idxY0][idxX0];
  const c10 = values[idxY0][idxX1];
  const c01 = values[idxY1][idxX0];
  const c11 = values[idxY1][idxX1];

  let c0: number;
  let c1: number;
  let c: number;
  
  if (idxX0 === idxX1) {
    c0 = c00;
    c1 = c01;
  } else {
    c0 = ((x1 - x) * c00 + (x - x0) * c10) / (x1 - x0);
    c1 = ((x1 - x) * c01 + (x - x0) * c11) / (x1 - x0);
  }

  if (idxY0 === idxY1) {
    c = c0;
  } else {
    c = ((y1 - y) * c0 + (y - y0) * c1) / (y1 - y0);
  }
  return c;
};

/**
 * Checks whether a value is in range
 * 
 * @param value - The value to check
 * @param min - The minimum allowed value (inclusive), or null for no minimum
 * @param max - The maximum allowed value (inclusive), or null for no maximum
 * @returns -1 if below range, 1 if above range, 0 if in range
 */
export const inRange = (
  value: number, 
  min: number | null = null, 
  max: number | null = null
): -1 | 0 | 1 => {
  return (min !== null && value < min) ? -1 : (max !== null && value > max) ? 1 : 0;
};

/**
 * Checks if a submatrix contains any zero values
 * 
 * @param matrix - The matrix to check
 * @param i1 - Start row index
 * @param j1 - Start column index
 * @param i2 - End row index
 * @param j2 - End column index
 * @returns True if the submatrix contains any zeros, false otherwise
 */
const containsZero = (
  matrix: NumberMatrix, 
  i1: number, 
  j1: number, 
  i2: number, 
  j2: number
): boolean => {
  const subMatrix = matrix.slice(i1, i2 + 1).map(row => row.slice(j1, j2 + 1));
  return subMatrix.some(row => row.includes(0));
};

/**
 * Calculates the sum of a submatrix using prefix sums
 * 
 * @param prefixSums - Precomputed prefix sums for each row
 * @param i1 - Start row index
 * @param j1 - Start column index
 * @param i2 - End row index
 * @param j2 - End column index
 * @returns The sum of the submatrix
 */
const getSubMatrixSum = (
  prefixSums: NumberMatrix, 
  i1: number, 
  j1: number, 
  i2: number, 
  j2: number
): number => {
  let sum = 0;
  for (let i = i1; i <= i2; i++) {
    sum += prefixSums[i][j2] - (j1 > 0 ? prefixSums[i][j1 - 1] : 0);
  }
  return sum;
};

/**
 * Finds the largest non-zero submatrix in a matrix
 * 
 * @param matrix - The input matrix
 * @returns An array [x1, y1, x2, y2] representing the top-left and bottom-right coordinates of the largest submatrix
 */
export const findLargest = (matrix: NumberMatrix): [number, number, number, number] => {
  const numRows = matrix.length;
  const numCols = matrix[0].length;
  let maxSum = 0;
  let maxSumTopLeft: [number, number] | null = null;
  let maxSumBottomRight: [number, number] | null = null;

  // Pre-compute the prefix sum for each row
  const prefixSums: NumberMatrix = [];
  for (let i = 0; i < numRows; i++) {
    const rowPrefixSums: number[] = [matrix[i][0]];
    for (let j = 1; j < numCols; j++) {
      rowPrefixSums.push(rowPrefixSums[j - 1] + matrix[i][j]);
    }
    prefixSums.push(rowPrefixSums);
  }

  // Try all possible sub-matrices
  for (let i1 = 0; i1 < numRows; i1++) {
    for (let j1 = 0; j1 < numCols; j1++) {
      for (let i2 = i1; i2 < numRows; i2++) {
        for (let j2 = j1; j2 < numCols; j2++) {
          if (containsZero(matrix, i1, j1, i2, j2)) { continue } // Skip sub-matrices that contain a 0
          const sum = getSubMatrixSum(prefixSums, i1, j1, i2, j2);
          if (sum > maxSum) {
            maxSum = sum;
            maxSumTopLeft = [j1, i1];
            maxSumBottomRight = [j2 + 1, i2 + 1];
          }
        }
      }
    }
  }
  return [
    ...(maxSumTopLeft || [0, 0]), 
    ...(maxSumBottomRight || [0, 0])
  ] as [number, number, number, number];
};

/**
 * Calculates a point and tangent angle along a quadratic Bezier curve
 * 
 * @param x - The x coordinate at which to evaluate the curve
 * @param points - Array of three control points [P0, P1, P2]
 * @returns Object with curveY (y-coordinate) and angle (tangent angle in degrees)
 */
export const quadraticBezier = (
  x: number, 
  points: [Point, Point, Point] = [] as unknown as [Point, Point, Point]
): BezierResult => {
  const [P0, P1, P2] = points;
  
  // Handle the case where points parameter is the string 'points' (from original code)
  if (!P0 || !P1 || !P2) {
    return { curveY: 0, angle: 0 };
  }
  
  const t = (x - P0.x) / (P2.x - P0.x); // Interpolation parameter
  const y = (1 - t) ** 2 * P0.y + 2 * t * (1 - t) * P1.y + t ** 2 * P2.y;
  const a = mapValues({ x: 0, y: 0 }, (v, k) => 
    2 * (1 - t) * (P1[k as keyof Point] - P0[k as keyof Point]) + 
    2 * t * (P2[k as keyof Point] - P1[k as keyof Point])
  );
  
  return {
    curveY: y,
    angle: Math.atan2(a.y, a.x) * 180 / Math.PI
  };
};

/**
 * Calculates the length of a side in a right triangle using the Pythagorean theorem
 * 
 * @param c - The length of the hypotenuse
 * @param a - The length of one side
 * @returns The length of the other side
 */
export const side = (c: number, a: number): number => {
  const difference = c * c - a * a;
  // Handle cases where hypotenuse is shorter than the given side
  // This can happen with poor acoustics settings where mic coverage is minimal
  if (difference < 0) {
    return 0;
  }
  return Math.sqrt(difference);
};

/**
 * Tolerance for the exact-multiple check in `snapDownToIncrement`.
 *
 * A value that IS an exact multiple of the increment usually doesn't divide cleanly in binary
 * floating point — 2.7432 / 0.1524 evaluates to 17.999999999999996, not 18 — so a bare
 * `Math.floor` would drop such a value a whole increment (9'0" → 8'6"). The gap between a true
 * quotient and its float representation is ~1e-15 here, so 1e-9 absorbs it with many orders of
 * magnitude to spare while staying far below the smallest increment in use (0.15 m).
 */
export const INCREMENT_EPSILON = 1e-9;

/**
 * Snaps a value DOWN to the nearest multiple of `increment`, rounded to 4 dp.
 *
 * Use this — never `Math.round(value / increment) * increment` — whenever the value being snapped
 * is a MAXIMUM (a mic's highest usable mount height, a clearance limit, a capacity ceiling).
 * Nearest-increment rounding can land ABOVE the limit, silently violating the constraint the
 * value exists to express. The concrete case this was extracted for: a Poor-acoustics ceiling
 * mic's optimal max of 8'10.6" rounded UP to a 9'0" mount, which drops its coverage radius to
 * 0.928 m — below the 1 m that "optimal max" is defined to guarantee — and inflated the default
 * mic count (a 20'x14' no-furniture Poor room seeded 12 mics instead of 6).
 *
 * @param value     The value to snap (e.g. a mount height in metres).
 * @param increment The step to snap to (e.g. 0.1524 m = 0.5 ft pole sections).
 * @returns The largest multiple of `increment` that does not exceed `value`, to 4 dp.
 *          Returns `value` unchanged if either argument is not a usable finite number.
 */
export const snapDownToIncrement = (value: number, increment: number): number => {
  if (!isFinite(value) || !isFinite(increment) || increment <= 0) {
    return value;
  }
  return Math.round(Math.floor(value / increment + INCREMENT_EPSILON) * increment * 10000) / 10000;
};

/**
 * Returns the number in an array that is closest to a target value
 * 
 * @param array - Array of numbers to search
 * @param target - Target value to find the closest match for
 * @returns The closest number in the array to the target value, or 0 if the array is empty after filtering
 */
export const closest = (array: number[], target: number): number => {
  const filteredArray = dropWhile(array, num => num === -1);
  return (isEmpty(filteredArray)) ? 0 : minBy(filteredArray, num => Math.abs(num - target)) || 0;
};

interface SpeakerAimingConstants {
  listenerHeight: number;
  minVerticalAngle: number;
  maxVerticalAngle: number;
}

interface SpeakerAimingResult {
  horAngle: number;
  verAngle: number;
}

/**
 * Calculates horizontal (azimuth) and vertical (elevation) aiming angles for a wall speaker.
 */
export const calculateAimingAngles = (
  speakerLocation: Point,
  targetPoint: Point,
  speakerHeight: number,
  constants: { area: { speakerAiming: SpeakerAimingConstants } }
): SpeakerAimingResult => {
  const dx = targetPoint.x - speakerLocation.x;
  const dy = targetPoint.y - speakerLocation.y;

  let horAngle = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (horAngle < 0) horAngle += 360;

  const horizontalDistance = Math.sqrt(dx * dx + dy * dy);
  const { listenerHeight, minVerticalAngle, maxVerticalAngle } = constants.area.speakerAiming;
  const heightDiff = speakerHeight - listenerHeight;
  let verAngle = -(Math.atan2(heightDiff, horizontalDistance) * 180) / Math.PI;
  verAngle = Math.max(minVerticalAngle, Math.min(maxVerticalAngle, verAngle));

  return {
    horAngle: Math.round(horAngle),
    verAngle: Math.round(verAngle * 10) / 10
  };
};

/**
 * Calculates the rotation for a wall speaker so it faces into the room.
 * 0° = right, 90° = down, 180° = left, 270° = up.
 */
export const calculateSpeakerRotation = (
  location: Point,
  roomLength: number,
  roomWidth: number
): number => {
  const tolerance = 0.5;
  if (location.x < tolerance) return 0;
  if (location.x > roomLength - tolerance) return 180;
  if (location.y < tolerance) return 90;
  if (location.y > roomWidth - tolerance) return 270;
  return 0;
};

/**
 * Maximum number of whole tiles that fit along a room dimension.
 *
 * Per PIG-278 spec: `Math.ceil(roomDim / tileSize)`, with a tiny epsilon to absorb
 * float-precision noise from unit conversion (e.g. 12 ft → 3.6576 m can round to a
 * value just over an exact tile multiple). Clamped to at least 1. Tiles that extend
 * partially past the room edge still count as a full tile.
 *
 * Shared by the ceiling-tile reducer, validation, selectors, and rendering — keep
 * the rule here so they all agree.
 */
export const calcMaxTilesFit = (roomDim: number, tileSize: number): number => {
  if (!Number.isFinite(roomDim) || !Number.isFinite(tileSize) || tileSize <= 0 || roomDim <= 0) {
    return 1;
  }
  const raw = roomDim / tileSize;
  const frac = raw - Math.floor(raw);
  // Use 2% fractional tolerance: room dimensions stored as 2dp meters (e.g. 3.66 instead of 3.6576)
  // can be fractionally just over a tile boundary; treat anything within 2% as fitting exactly.
  return Math.max(1, frac < 0.02 ? Math.floor(raw) : Math.ceil(raw));
};

/**
 * Calculates the angle formed by three points
 *
 * @param pointA - First point
 * @param pointB - Middle point (vertex of the angle)
 * @param pointC - Last point
 * @returns The angle in degrees
 */
export const findAngle = (pointA: Point, pointB: Point, pointC: Point): number => {
  const AB = Math.hypot((pointB.x - pointA.x), (pointB.y - pointA.y));
  const BC = Math.hypot((pointB.x - pointC.x), (pointB.y - pointC.y));
  const AC = Math.hypot((pointC.x - pointA.x), (pointC.y - pointA.y));
  return Math.acos(clamp((BC * BC + AB * AB - AC * AC) / (2 * BC * AB), -1, 1)) * (180 / Math.PI);
};
